import { useRecoilValue } from 'recoil'

import { PersonaType } from '@/modules/auth/domain/models/Persona'
import { PersonaInfoSummary } from '@/modules/shared/schemas/PersonaInfoSummary'
import { Permissions } from '@/shared/helpers/permissions'
import { userState } from '@/shared/state/user/user'

import { useAuthContext } from '../contexts/AuthContext'
import { useUserPermissionQuery } from './queries/useUserPermissionQuery'

export function useUserPermission() {
  const { personaSession } = useAuthContext()
  const { personaId: recoilPersonaId, personaType: recoilPersonaType } =
    useRecoilValue(userState)

  const personaId = personaSession?.personaId ?? recoilPersonaId
  const personaType = personaSession?.personaType ?? recoilPersonaType

  const userPermission = useUserPermissionQuery({
    personaId
  })

  const userManagers = new Map<string, PersonaInfoSummary[]>(
    userPermission.data?.permissions.map(({ personaId, patientManager }) => [
      personaId,
      patientManager
    ]) ?? []
  )

  const userPermissions = userPermission.data?.permissions.reduce(
    (acc, { personaType, permissions }) => {
      acc[personaType] = permissions
      return acc
    },
    {} as { [key in PersonaType]?: Permissions[] }
  )

  return {
    checkPermission(permission: Permissions): boolean {
      if (!userPermissions) return false
      const permissionList = userPermissions[personaType]
      if (!permissionList) return false
      return permissionList.some(
        (userPermission) => userPermission === permission
      )
    },

    checkIsPatientManager(
      permission: Permissions,
      patientId?: string
    ): boolean {
      if (!patientId || !userPermissions || !userManagers) return false

      const permissionList = userPermissions[personaType]
      if (!permissionList || !permissionList.includes(permission)) return false

      const patients = userManagers.get(personaId)
      return patients?.some((patient) => patient.userId === patientId) ?? false
    }
  }
}
